import { Badge, Box, FormControl, MenuItem, Select } from "@material-ui/core"
import moment from "moment"
import type React from "react"
import type { ReactElement, ReactNode } from "react"
import { useMemo, useState } from "react"

import type { ProjectComparisonDto, ProjectDto } from "../../../models"
import { Body1, CardTemplateContainer, Header3 } from "../../atoms"
import { ComparisonCard, ProjectCard, TabContainer, TabContentContainer } from "../../molecules"

import "./comparison-and-project-overview.styles.scss"
import { useTranslation } from "react-i18next"
import type { IAssignmentDialogProps } from "../assignment-dialog/assignment-dialog.component"
import { AssignmentDialog } from "../assignment-dialog/assignment-dialog.component"
import { useAuthenticatedUser } from "../../indentity-provider.component"

export interface IComparisonAndProjectOverviewProps {
  disabled?: boolean
  projects: ProjectDto[]
  comparisons: ProjectComparisonDto[]
  sharedProjects?: ProjectDto[]
  sharedComparisons?: ProjectComparisonDto[]
  selectable?: boolean
  disableEditing?: boolean | undefined
  showInFolder?: boolean | undefined
  disablePreview?: boolean | undefined
  shared?: boolean | undefined
  isProjectSelected?: (project: ProjectDto) => boolean
  isComparisonSelected?: (comparison: ProjectComparisonDto) => boolean
  onProjectClick?: (project: ProjectDto) => void
  onProjectChangeBundle?: (project: ProjectDto) => void
  onProjectRemove?: (project: ProjectDto) => void
  onProjectUpdate?: (project: ProjectDto) => void
  onComparisonClick?: (comparison: ProjectComparisonDto) => void
  onComparisonChangeBundle?: (comparison: ProjectComparisonDto) => void
  onComparisonRemove?: (comparison: ProjectComparisonDto) => void
  onComparisonUpdate?: (comparison: ProjectComparisonDto) => void
  onProjectPreview?: (project: ProjectDto) => void
}

type SortingFunction = (first: ProjectDto | ProjectComparisonDto, second: ProjectDto | ProjectComparisonDto) => number

export function ComparisonAndProjectOverview(props: IComparisonAndProjectOverviewProps): ReactElement {
  const {
    disabled,
    projects,
    comparisons,
    sharedProjects,
    sharedComparisons,
    selectable,
    disableEditing,
    showInFolder,
    disablePreview,
    shared,
    isProjectSelected,
    isComparisonSelected,
    onProjectClick,
    onProjectChangeBundle,
    onProjectRemove,
    onProjectUpdate,
    onComparisonClick,
    onComparisonChangeBundle,
    onComparisonRemove,
    onComparisonUpdate,
    onProjectPreview
  } = props

  const { t } = useTranslation(["myBosatlas", "general"])

  const { isTeacher } = useAuthenticatedUser()

  const [activeTab, setActiveTab] = useState<number>(0)
  const [sorting, setSorting] = useState<number>(shared ? 2 : 0)
  const [assignmentDialogProps, setAssignmentDialogProps] = useState<IAssignmentDialogProps>({
    open: false,
    objectDescriptor: "map",
    shouldSave: false,
    onClose: closeAssignmentDialog,
    onSave: () => {}
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const combinedSharedList: (ProjectDto | ProjectComparisonDto)[] = useMemo<
    (ProjectDto | ProjectComparisonDto)[]
  >(() => {
    const sharedProjectsArr: ProjectDto[] = sharedProjects ? sharedProjects : []
    const sharedComparisonsArr: ProjectComparisonDto[] = sharedComparisons ? sharedComparisons : []
    return [...sharedProjectsArr, ...sharedComparisonsArr].sort(getSortingFunction())
  }, [sharedProjects, sharedComparisons, sorting])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const combinedList: (ProjectDto | ProjectComparisonDto)[] = useMemo<(ProjectDto | ProjectComparisonDto)[]>(() => {
    return [...projects, ...comparisons, ...combinedSharedList].sort(getSortingFunction())
  }, [projects, comparisons, sorting])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const sortedProjects: ProjectDto[] = useMemo<ProjectDto[]>(() => {
    return [...projects].sort(getSortingFunction())
  }, [projects, sorting])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const sortedComparisons: ProjectComparisonDto[] = useMemo<ProjectComparisonDto[]>(() => {
    return [...comparisons].sort(getSortingFunction())
  }, [comparisons, sorting])

  function handleSortingChange(event: React.ChangeEvent<{ value: unknown }>): void {
    setSorting(event.target.value as number)
  }

  function closeAssignmentDialog(): void {
    setAssignmentDialogProps({
      ...assignmentDialogProps,
      open: false
    })
  }
  function openAssignmentDialog(type: "project" | "comparison", id: string): void {
    const url: string = `${window.location.origin}/atlas/${type}/${id}`
    setAssignmentDialogProps({
      ...assignmentDialogProps,
      open: true,
      objectDescriptor: type === "project" ? "map" : "comparison",
      url
    })
  }
  function onShareProjectUrl(projectId: string): void {
    openAssignmentDialog("project", projectId)
  }

  function onShareComparisonUrl(comparisonId: string): void {
    openAssignmentDialog("comparison", comparisonId)
  }

  function getSortingFunction(): SortingFunction {
    let sortingFunction: SortingFunction = sortAscending
    if (sorting === 1) sortingFunction = sortDescending
    if (sorting === 2) sortingFunction = sortByModifiedDate
    return sortingFunction
  }

  function sortAscending(first: ProjectDto | ProjectComparisonDto, second: ProjectDto | ProjectComparisonDto): number {
    return (first.title?.toLowerCase() || "") <= (second.title?.toLowerCase() || "") ? -1 : 1
  }

  function sortDescending(first: ProjectDto | ProjectComparisonDto, second: ProjectDto | ProjectComparisonDto): number {
    return (first.title?.toLowerCase() || "") >= (second.title?.toLowerCase() || "") ? -1 : 1
  }

  function sortByModifiedDate(
    first: ProjectDto | ProjectComparisonDto,
    second: ProjectDto | ProjectComparisonDto
  ): number {
    const firstDate = moment(first.lastModified?.toString())
    const secondDate = moment(second.lastModified?.toString())

    if (!first.lastModified && second.lastModified) return 1
    if (first.lastModified && !second.lastModified) return -1

    return firstDate.isBefore(secondDate) ? 1 : -1
  }

  function CardComponent(props: {
    item: ProjectDto | ProjectComparisonDto
  }): ReactElement {
    const { item } = props

    if ("firstId" in item) {
      const comparison: ProjectComparisonDto = item as ProjectComparisonDto
      return (
        <ComparisonCard
          selectable={selectable}
          selected={isComparisonSelected ? isComparisonSelected(comparison) : undefined}
          disableEditing={disableEditing}
          onShareUrlClick={onShareComparisonUrl}
          showInFolder={showInFolder}
          comparison={comparison}
          onOpenComparison={onComparisonClick}
          onChangeBundle={onComparisonChangeBundle}
          onRemove={onComparisonRemove}
          onRequestUpdate={onComparisonUpdate}
        />
      )
    }

    const project: ProjectDto = item as ProjectDto

    return (
      <ProjectCard
        disabled={disabled}
        selectable={selectable}
        selected={isProjectSelected ? isProjectSelected(project) : undefined}
        disableEditing={disableEditing}
        disablePreview={disablePreview}
        onShareUrlClick={onShareProjectUrl}
        onProjectClick={onProjectClick}
        onChangeBundle={onProjectChangeBundle}
        onRemove={onProjectRemove}
        onRequestUpdate={onProjectUpdate}
        project={project}
        onProjectPreview={onProjectPreview}
      />
    )
  }

  function getTabElements(): ReactNode[] {
    const tabElements = [
      <Box key="all-maps" className="comparison-project-tab">
        {t("myBosatlas:storedMaps.all")}
        <Badge className="comparison-project-tab-badge" badgeContent={combinedList.length} color="primary" />
      </Box>,
      <Box key="storedMaps" className="comparison-project-tab">
        {t("myBosatlas:storedMaps.maps")}
        <Badge className="comparison-project-tab-badge" badgeContent={projects.length} color="primary" />
      </Box>,
      <Box key="mapsComparisons" className="comparison-project-tab">
        {t("myBosatlas:storedMaps.comparisons")}
        <Badge className="comparison-project-tab-badge" badgeContent={comparisons.length} color="primary" />
      </Box>
    ]

    if (isTeacher && combinedSharedList.length > 0) {
      tabElements.push(
        <Box className="comparison-project-tab">
          {t("myBosatlas:storedMaps.shared")}
          <Badge className="comparison-project-tab-badge" badgeContent={combinedSharedList.length} color="primary" />
        </Box>
      )
    }

    return tabElements
  }

  return (
    <Box className="comparison-and-project-overview">
      <Header3>{shared ? t("myBosatlas:storedMaps.sharedMe") : t("myBosatlas:storedMaps.storedMapsTitle")}</Header3>
      <Box className={`comparison-and-project-overview-header ${disableEditing ? "flex-end" : ""}`}>
        {!disableEditing && <TabContainer tabs={getTabElements()} activeTab={activeTab} onTabChange={setActiveTab} />}
        <Box className="comparison-and-project-sorting">
          <Body1>{t("general:sort.sortTitle")}</Body1>
          <FormControl variant="outlined">
            <Select
              margin="dense"
              onChange={handleSortingChange}
              value={sorting}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left"
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left"
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value={0}>{t("general:sort.nameAscending")}</MenuItem>
              <MenuItem value={1}>{t("general:sort.nameDescending")}</MenuItem>
              <MenuItem value={2}>{shared ? t("general:sort.lastReceived") : t("general:sort.lastSaved")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <TabContentContainer activeTab={activeTab}>
        <CardTemplateContainer columns={4}>
          {combinedList.map((item: ProjectDto | ProjectComparisonDto) => (
            <CardComponent key={item.id} item={item} />
          ))}
        </CardTemplateContainer>
        <CardTemplateContainer columns={4}>
          {sortedProjects.map((item: ProjectDto) => (
            <CardComponent key={item.id} item={item} />
          ))}
        </CardTemplateContainer>
        <CardTemplateContainer columns={4}>
          {sortedComparisons.map((item: ProjectComparisonDto) => (
            <CardComponent key={item.id} item={item} />
          ))}
        </CardTemplateContainer>
        {isTeacher && combinedSharedList.length > 0 ? (
          <CardTemplateContainer columns={4}>
            {combinedSharedList.map((item: ProjectDto | ProjectComparisonDto) => (
              <CardComponent key={item.id} item={item} />
            ))}
          </CardTemplateContainer>
        ) : (
          <></>
        )}
      </TabContentContainer>
      <AssignmentDialog {...assignmentDialogProps} />
    </Box>
  )
}
